/**
 * Usage Service
 * Handles business logic for usage tracking data
 */
import { getTotalTimeSpent } from '../api/usage/usageApi';
import api from '../axiosconfig';

/**
 * Get total chat usage in minutes for a date range
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @returns {Promise<number>} Total minutes rounded to 1 decimal place
 */
export const getChatUsageTotal = async (startDate, endDate) => {
  try {
    const response = await getTotalTimeSpent({
      interface: 'chat',
      startDate,
      endDate,
      includeBreakdown: false
    });

    // Support both camelCase and snake_case response formats
    const rawMinutes = response?.totalMinutes ?? response?.total_minutes ?? 0;
    const numericMinutes = Number(rawMinutes) || 0;
    return Number(numericMinutes.toFixed(1));
  } catch (error) {
    console.error('Error fetching chat usage total:', error);
    // Return 0.0 on error
    return 0.0;
  }
};

/**
 * Get total assessment questions answered for the authenticated user in a date range
 * @param {number|string} userId - User ID
 * @param {string} fromDate - Start date/time in ISO format
 * @param {string} toDate - End date/time in ISO format
 * @returns {Promise<number>} Questions answered
 */
export const getUserQuestionsAnsweredTotal = async (userId, fromDate, toDate) => {
  try {
    if (!userId || !fromDate || !toDate) return 0;

    const response = await api.get('/api/assessments/questions-answered', {
      params: {
        userId,
        fromDate,
        toDate,
      },
    });

    const rawValue = response?.data?.questionsAnswered;
    return Number(rawValue) || 0;
  } catch (error) {
    console.error('Error fetching personal questions answered:', error);
    return 0;
  }
};
